import { useState, forwardRef, Ref } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { MagnifyingGlass } from "@phosphor-icons/react/dist/ssr";

// Options with label === "__separator" are rendered as horizontal line.
export const SEPARATOR = "__separator";

type SelectorType = {
  label: string;
  // alias is used as a searchable alternative name for the option.
  // it won't be displayed, but search will be performed on it.
  alias?: string;
  render?: (option: any) => React.ReactNode;
};

type FilterSelect<T extends SelectorType> = {
  hideSearch?: boolean;
  options: T[];
  selectedOption: T | null;
  onOptionSelect: (selectedOption: T) => void;
  /** Display '...' next to the option label to
   * indicate that there is a follow-up action after selecting the option */
  threeDots?: boolean;
};

export const FilterTargetSelector = <T extends SelectorType>({
  options,
  selectedOption,
  onOptionSelect,
  hideSearch = false,
  threeDots = false,
}: FilterSelect<T>) => {
  const [search, setSearch] = useState("");
  const filteredOptions = search.trim()
    ? options.filter(
        (option) =>
          (option.label.toLowerCase().includes(search.toLowerCase()) ||
            option.alias?.toLowerCase().includes(search.toLowerCase())) &&
          option.label !== "SEPARATOR",
      )
    : options;

  return (
    <div>
      {!hideSearch && (
        <>
          <div className="mb-4 mt-4 flex h-28 grow items-center outline-none focus:outline-none">
            {/* Ok this is a big hack, to make the search input take the focus initially, and still 
          allow keybaord nav between other dropdown items. Really this should be a command combobox, 
          but i think that needs us to pull out the design of the dropdown items better. Right now it doesn't work
          ideally with up/down navigation as that loses focus.  */}
            <DropdownMenu.Item
              asChild
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.stopPropagation();
                }
                if (e.key === "Escape") {
                  if (search.length > 0) {
                    setSearch("");
                  } else {
                    // TODO: ideally close here.
                  }
                }
              }}
              onSelect={(e) => {
                // Do nothing and don't close! Without this clicking on search would close the dropdown.
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <SearchInputComponent search={search} setSearch={setSearch} />
            </DropdownMenu.Item>
          </div>
          <DropdownMenu.Separator className="h-[1px] w-full border border-stroke-base-2" />
        </>
      )}

      <div className="flex flex-col gap-2 overflow-hidden pb-4 pt-4">
        {filteredOptions.length > 0 ? (
          filteredOptions.map((option, index) => (
            <div key={`${option.label}-${index}`}>
              {option.label === SEPARATOR ? (
                <DropdownMenu.Separator className="my-2 h-[1px] w-full border border-stroke-base-2" />
              ) : (
                <DropdownMenu.Item
                  onClick={() => onOptionSelect(option)}
                  className={`group mx-4
                  cursor-pointer items-center 
                  rounded-ms
                  focus:bg-background-base-3
                  focus:outline-none
                  focus:ring-0 active:z-10
                  active:!bg-background-base-1
                  active:ring-2
                  active:ring-stroke-secondary-2
                  ${selectedOption?.label === option.label ? "!bg-background-secondary-1 focus:!bg-background-secondary-2" : ""}`}
                >
                  <div className="flex h-28 items-center px-12 focus-within:ring-0 focus:ring-0">
                    {option.render ? (
                      option.render(option)
                    ) : (
                      <span className="text-12-12 font-medium text-text-base-1">
                        {option.label}{" "}
                        {threeDots ? <span className="text-12-12 font-medium text-text-base-2">...</span> : ""}
                      </span>
                    )}
                  </div>
                </DropdownMenu.Item>
              )}
            </div>
          ))
        ) : (
          <div className="flex h-28 items-center justify-start pl-12">
            <span className="text-12-12 font-normal text-text-base-3">No options found</span>
          </div>
        )}
      </div>
    </div>
  );
};

const SearchInputComponent = forwardRef(
  (
    { search, setSearch, ...props }: { search: string; setSearch: (search: string) => void },
    ref: Ref<HTMLInputElement>,
  ) => {
    return (
      <div className="flex items-center justify-start px-12">
        <MagnifyingGlass weight="bold" size={14} className="ml-4 shrink-0 text-icon-base-3" />
        <input
          autoFocus
          ref={ref}
          type="text"
          className="flex h-[26px] w-full items-start border-none  !pl-6  text-left text-12-16 font-normal text-text-base-1 outline-none scrollbar-none focus-within:ring-0 focus:ring-0"
          placeholder={"Search"}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          {...props}
        />
      </div>
    );
  },
);

SearchInputComponent.displayName = "SearchInputComponent";
